import { create } from 'zustand';
import { getMyInfo, refreshToken as refreshTokenAPI, logout as logoutAPI } from '../lib/api/user-api';

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';
const LOGIN_FLAG_KEY = 'isLoggedIn';

const safeParse = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const readStoredAuth = () => ({
  token: localStorage.getItem(TOKEN_KEY),
  refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
  user: safeParse(localStorage.getItem(USER_KEY)),
});

const persistAuth = ({ token, refreshToken, user }) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_KEY);
  }

  if (token && user) {
    localStorage.setItem(LOGIN_FLAG_KEY, 'true');
  } else {
    localStorage.removeItem(LOGIN_FLAG_KEY);
  }
};

const clearStoredAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(LOGIN_FLAG_KEY);
};

const initialAuth = readStoredAuth();

export const useAuthStore = create((set, get) => ({
  ...initialAuth,
  isLoggedIn: !!initialAuth.token && !!initialAuth.user,
  loading: true,
  initialized: false,

  setAuth: ({ token, refreshToken, user }) => {
    const next = {
      token: token ?? null,
      refreshToken: refreshToken ?? null,
      user: user ?? null,
      isLoggedIn: !!(token && user),
    };
    persistAuth(next);
    set(next);
  },

  login: (tokenOrPayload, userData = null, refreshToken = null) => {
    if (typeof tokenOrPayload === 'object' && tokenOrPayload !== null) {
      const payload = tokenOrPayload;
      get().setAuth({
        token: payload.token ?? null,
        refreshToken: payload.refreshToken ?? null,
        user: payload.user ?? null,
      });
      return;
    }

    get().setAuth({
      token: tokenOrPayload ?? null,
      refreshToken: refreshToken ?? null,
      user: userData ?? null,
    });
  },

  logout: async () => {
    const currentRefresh = get().refreshToken;
    if (currentRefresh) {
      try {
        await logoutAPI(currentRefresh);
      } catch (error) {
        console.warn('로그아웃 API 실패:', error);
      }
    }

    clearStoredAuth();
    set({
      token: null,
      refreshToken: null,
      user: null,
      isLoggedIn: false,
    });
  },

  refreshAccessToken: async () => {
    const currentRefreshToken = get().refreshToken;
    if (!currentRefreshToken) {
      return false;
    }

    try {
      const response = await refreshTokenAPI(currentRefreshToken);
      if (!response?.success || !response?.data?.token) {
        await get().logout();
        return false;
      }

      get().setAuth({
        token: response.data.token,
        refreshToken: response.data.refreshToken ?? currentRefreshToken,
        user: response.data.user ?? get().user,
      });
      return true;
    } catch (error) {
      await get().logout();
      return false;
    }
  },

  refreshUserInfo: async () => {
    let currentToken = get().token;
    if (!currentToken) {
      return null;
    }

    try {
      const response = await getMyInfo(currentToken);
      if (response?.success) {
        get().setAuth({
          token: get().token,
          refreshToken: get().refreshToken,
          user: response.data,
        });
        return response.data;
      }
      return null;
    } catch (error) {
      const status = error?.status;
      if (status === 401) {
        const refreshed = await get().refreshAccessToken();
        if (!refreshed) {
          return null;
        }

        currentToken = get().token;
        const retry = await getMyInfo(currentToken);
        if (retry?.success) {
          get().setAuth({
            token: get().token,
            refreshToken: get().refreshToken,
            user: retry.data,
          });
          return retry.data;
        }
      }

      throw error;
    }
  },

  initializeAuth: async () => {
    if (get().initialized) {
      return;
    }

    const { token, refreshToken, user } = readStoredAuth();
    if (!token || !user) {
      clearStoredAuth();
      set({ token: null, refreshToken: null, user: null, isLoggedIn: false, loading: false, initialized: true });
      return;
    }

    set({ token, refreshToken, user, isLoggedIn: true, loading: true });

    try {
      const info = await get().refreshUserInfo();
      if (!info) {
        clearStoredAuth();
        set({ token: null, refreshToken: null, user: null, isLoggedIn: false });
      }
    } catch (error) {
      clearStoredAuth();
      set({ token: null, refreshToken: null, user: null, isLoggedIn: false });
    } finally {
      set({ loading: false, initialized: true });
    }
  },
}));
